import { ExerciseOrSuperset, RoutineExercise } from '../../../types/Fisiofit/routine';
import { EXERCISE_TYPE, ExerciseShort } from '../../../types/Fisiofit/exercise';
import { isSuperset, canCreateSupersetWithNext, canCreateSupersetWithPrevious } from '../../../utils/supersetUtils';
import { RoutineForm } from '../validators';
import { findFirstExerciseWithId, isDurationExercise, isRepetitionExercise } from '../utils/exercises';

interface UseRoutineSupersetsOptions {
  form: RoutineForm;
  setFormFields: (fields: DeepPartial<RoutineForm>) => void;
  organizationExercises: ExerciseShort[] | undefined;
}

export const useRoutineSupersets = ({ form, setFormFields, organizationExercises }: UseRoutineSupersetsOptions) => {
  // Función para eliminar un ejercicio de una superserie
  const removeSupersetExercise = (dayIndex: number, exerciseIndex: number, supersetIndex: number) => {
    if (!form?.exerciseDays[dayIndex]) return;

    const exerciseOrSuperset = form.exerciseDays[dayIndex].exercises[exerciseIndex];
    if (!isSuperset(exerciseOrSuperset)) return;

    // Crear una copia profunda del array de días para garantizar detección de cambios
    const updatedDays = JSON.parse(JSON.stringify(form.exerciseDays));
    const exercises = updatedDays[dayIndex].exercises;
    const superset = exercises[exerciseIndex] as RoutineExercise[];

    // Determinar qué ejercicio mantener (el que NO se va a eliminar)
    const exerciseToKeep = superset[supersetIndex === 0 ? 1 : 0];

    // Reemplazar la superserie con el ejercicio que se mantiene
    const newExercises = [
      ...exercises.slice(0, exerciseIndex),
      exerciseToKeep,
      ...exercises.slice(exerciseIndex + 1),
    ] as ExerciseOrSuperset[];

    updatedDays[dayIndex] = {
      ...updatedDays[dayIndex],
      exercises: newExercises,
    };

    // Actualizar el estado del formulario con los días actualizados
    setFormFields({ exerciseDays: updatedDays });
  };

  // Función para crear una superserie
  const handleCreateSuperset = (dayIndex: number, exerciseIndex: number, direction: 'next' | 'previous' = 'next') => {
    if (!form?.exerciseDays[dayIndex]) return;

    const exercises = form.exerciseDays[dayIndex].exercises;

    // Verificar si podemos crear la superserie en la dirección especificada
    if (direction === 'next' && !canCreateSupersetWithNext(exercises, exerciseIndex)) return;
    if (direction === 'previous' && !canCreateSupersetWithPrevious(exercises, exerciseIndex)) return;

    const updatedDays = [...form.exerciseDays];

    let exercise1: RoutineExercise;
    let exercise2: RoutineExercise;
    let targetIndex: number;
    let removeCount: number;

    if (direction === 'next') {
      exercise1 = exercises[exerciseIndex] as RoutineExercise;
      exercise2 = exercises[exerciseIndex + 1] as RoutineExercise;
      targetIndex = exerciseIndex;
      removeCount = 2;
    } else {
      exercise1 = exercises[exerciseIndex - 1] as RoutineExercise;
      exercise2 = exercises[exerciseIndex] as RoutineExercise;
      targetIndex = exerciseIndex - 1;
      removeCount = 2;
    }

    const superset: RoutineExercise[] = [exercise1, exercise2];

    // Reemplazar los dos ejercicios individuales con la superserie
    const newExercises = [
      ...exercises.slice(0, targetIndex),
      superset,
      ...exercises.slice(targetIndex + removeCount),
    ] as ExerciseOrSuperset[];

    updatedDays[dayIndex] = {
      ...updatedDays[dayIndex],
      exercises: newExercises,
    };

    setFormFields({ exerciseDays: updatedDays });
  };

  // Función para romper una superserie
  const handleBreakSuperset = (dayIndex: number, exerciseIndex: number) => {
    if (!form?.exerciseDays[dayIndex]) {
      return;
    }

    const exerciseOrSuperset = form.exerciseDays[dayIndex].exercises[exerciseIndex];
    if (!isSuperset(exerciseOrSuperset)) {
      return;
    }

    const updatedDays = [...form.exerciseDays];
    const exercises = updatedDays[dayIndex].exercises;
    const superset = exercises[exerciseIndex] as RoutineExercise[];

    // Dividir la superserie en ejercicios individuales
    const newExercises = [
      ...exercises.slice(0, exerciseIndex),
      superset[0],
      superset[1],
      ...exercises.slice(exerciseIndex + 1),
    ] as ExerciseOrSuperset[];

    updatedDays[dayIndex] = {
      ...updatedDays[dayIndex],
      exercises: newExercises,
    };

    setFormFields({ exerciseDays: updatedDays });
  };

  // Función para actualizar un ejercicio dentro de una superserie
  const updateSupersetExercise = (
    dayIndex: number,
    exerciseIndex: number,
    supersetIndex: number,
    updatedFields: Partial<RoutineExercise>,
  ) => {
    const updatedDays = JSON.parse(JSON.stringify(form.exerciseDays));
    const exercises = updatedDays[dayIndex].exercises;
    const superset = exercises[exerciseIndex] as RoutineExercise[];

    // Actualizar el ejercicio específico dentro de la superserie
    const updatedExercise = {
      ...superset[supersetIndex],
      ...updatedFields,
    };
    superset[supersetIndex] = updatedExercise;

    // Actualizar todos los ejercicios duplicados en el mismo día, manteniendo la estructura de superseries
    updatedDays[dayIndex].exercises = exercises.map((exerciseOrSuperset: ExerciseOrSuperset, index: number) => {
      if (index === exerciseIndex) {
        // Mantener la superserie actualizada
        return superset;
      }

      if (isSuperset(exerciseOrSuperset)) {
        // Actualizar ejercicios duplicados dentro de otras superseries
        const updatedSuperset = exerciseOrSuperset.map((exercise) => {
          if (exercise.exerciseId === updatedExercise.exerciseId) {
            return { ...exercise, ...updatedFields };
          }
          return exercise;
        });
        return updatedSuperset;
      } else if ((exerciseOrSuperset as RoutineExercise).exerciseId === updatedExercise.exerciseId) {
        // Actualizar ejercicios individuales duplicados
        return { ...exerciseOrSuperset, ...updatedFields };
      }

      return exerciseOrSuperset;
    });

    // Actualizar el estado del formulario
    setFormFields({ exerciseDays: updatedDays });
  };

  // Función para manejar la selección de ejercicio en superserie
  const handleSupersetExerciseSelect = (dayIndex: number, exerciseIndex: number, supersetIndex: number, selectedId: string) => {
    const selectedExercise = organizationExercises?.find((e) => e.id === selectedId);
    if (selectedExercise) {
      // Buscar si ya existe un ejercicio con el mismo ID en este día
      const existingExercise = findFirstExerciseWithId(form.exerciseDays[dayIndex].exercises, selectedId);

      let updatedValues;
      if (existingExercise) {
        // Si ya existe, copiar todos los valores del ejercicio existente
        updatedValues = {
          exerciseId: selectedExercise.id,
          name: selectedExercise.name,
          exerciseType: selectedExercise.exerciseType,
          imageUrl: selectedExercise.imageUrl,
          videoUrl: selectedExercise.videoUrl,
          sets: existingExercise.sets,
          weight: existingExercise.weight,
          rir: existingExercise.rir,
          rpe: existingExercise.rpe,
          comment: existingExercise.comment,
          ...(selectedExercise.exerciseType === EXERCISE_TYPE.TIME
            ? {
                repetitions: undefined,
                duration: isDurationExercise(existingExercise) ? existingExercise.duration : undefined,
              }
            : {}),
          ...(selectedExercise.exerciseType === EXERCISE_TYPE.REPS
            ? {
                duration: undefined,
                repetitions: isRepetitionExercise(existingExercise) ? existingExercise.repetitions : undefined,
              }
            : {}),
        };
      } else {
        // Si es la primera vez, usar valores por defecto
        updatedValues = {
          exerciseId: selectedExercise.id,
          name: selectedExercise.name,
          exerciseType: selectedExercise.exerciseType,
          imageUrl: selectedExercise.imageUrl,
          videoUrl: selectedExercise.videoUrl,
          ...(selectedExercise.exerciseType === EXERCISE_TYPE.TIME ? { repetitions: undefined } : {}),
          ...(selectedExercise.exerciseType === EXERCISE_TYPE.REPS ? { duration: undefined } : {}),
        };
      }

      updateSupersetExercise(dayIndex, exerciseIndex, supersetIndex, updatedValues);
    }
  };

  return {
    removeSupersetExercise,
    handleCreateSuperset,
    handleBreakSuperset,
    updateSupersetExercise,
    handleSupersetExerciseSelect,
  };
};
